const React = require('react');
const { Controller } = require('react-hook-form');

const FieldTypeUtils = require('./fieldTypes');
const FormFieldUtils = require('./formFieldUtils');
const InvalidSubmitFocusUtils = require('./invalidSubmitFocusUtils');
const AgeField = require('./AgeField');
const AutocompleteField = require('./AutocompleteField');
const CheckboxField = require('./CheckboxField');
const DateField = require('./DateField');
const DateTimeField = require('./DateTimeField');
const EmojiField = require('./EmojiField');
const FileField = require('./FileField');
const MultiSelectField = require('./MultiSelectField');
const PlaceField = require('./PlaceField');
const SelectField = require('./SelectField');
const SwitchField = require('./SwitchField');
const TextareaField = require('./TextareaField');
const TextField = require('./TextField');
const ConnectedInterestsInputField = require('containers/InterestsInputField');

const Typography = require('@mui/material/Typography').default;
const Divider = require('@mui/material/Divider').default;

const internals = {};
const DEFAULT_FIELD_VARIANT = 'filled';
const DEFAULT_FIELD_LABEL = 'Form field';

exports.buildFieldRules = (field = {}) => {

    return {
        ...field.rules,
        required: field.required ? `${field.label} is required` : false
    };
};

internals.getTextLabel = (label) => {

    if (typeof label === 'string') {
        return label;
    }

    if (typeof label === 'number') {
        return String(label);
    }

    return '';
};

exports.getAccessibleFieldLabel = (field = {}) => {

    return internals.getTextLabel(field.ariaLabel)
        || internals.getTextLabel(field['aria-label'])
        || internals.getTextLabel(field.accessibleLabel)
        || internals.getTextLabel(field.label)
        || internals.getTextLabel(field.definition?.label)
        || field.name
        || DEFAULT_FIELD_LABEL;
};

internals.getFieldDomId = (field = {}) => field.id || field.inputId || field.name;

internals.getFieldLabelId = (field = {}) => field.labelId || (field.name ? `${field.name}-label` : undefined);

exports.buildFieldInputProps = ({
    field = {},
    includeId = false,
    inputProps = {},
    labelId
} = {}) => {

    const id = includeId ? internals.getFieldDomId(field) : undefined;

    return {
        ...(id ? { id } : {}),
        ...(field.name ? { name: field.name } : {}),
        'aria-label': exports.getAccessibleFieldLabel(field),
        ...(labelId ? { 'aria-labelledby': labelId } : {}),
        ...inputProps
    };
};

exports.buildFieldInputLabelProps = ({
    inputLabelProps = {},
    labelId
} = {}) => ({
    ...(labelId ? { id: labelId } : {}),
    ...inputLabelProps
});

exports.buildFieldAccessibilityProps = ({
    field = {},
    textFieldProps = {}
} = {}) => {

    const {
        InputLabelProps: providedInputLabelProps = {},
        inputProps: providedInputProps = {},
        ...restTextFieldProps
    } = textFieldProps;
    const labelId = providedInputLabelProps.id || internals.getFieldLabelId(field);
    const id = restTextFieldProps.id || internals.getFieldDomId(field);

    return {
        ...(id ? { id } : {}),
        ...(field.name ? { name: field.name } : {}),
        ...(typeof field.label !== 'undefined' ? { label: field.label } : {}),
        ...restTextFieldProps,
        InputLabelProps: exports.buildFieldInputLabelProps({
            inputLabelProps: providedInputLabelProps,
            labelId
        }),
        inputProps: exports.buildFieldInputProps({
            field,
            inputProps: providedInputProps,
            labelId
        })
    };
};

exports.buildTextFieldProps = ({
    field = {},
    fieldState = {},
    extraProps = {}
}) => {

    return {
        placeholder: field.placeholder,
        error: !!fieldState.error,
        helperText: fieldState.error ? fieldState.error.message : null,
        required: field.required,
        variant: DEFAULT_FIELD_VARIANT,
        ...exports.buildFieldAccessibilityProps({
            field,
            textFieldProps: extraProps
        })
    };
};

internals.revalidateOnBlurFieldsByForm = new WeakMap();
internals.fallbackRevalidateOnBlurFields = new Set();

internals.getRevalidateOnBlurFields = (methods) => {

    if (!methods || typeof methods !== 'object') {
        return internals.fallbackRevalidateOnBlurFields;
    }

    const existingFields = internals.revalidateOnBlurFieldsByForm.get(methods);

    if (existingFields) {
        return existingFields;
    }

    const fields = new Set();
    internals.revalidateOnBlurFieldsByForm.set(methods, fields);
    return fields;
};

internals.hasFieldError = ({
    field = {},
    fieldState = {},
    methods = {}
}) => {

    if (fieldState.error) {
        return true;
    }

    if (typeof methods.getFieldState !== 'function' || !field.name) {
        return false;
    }

    return !!methods.getFieldState(field.name)?.error;
};

internals.clearFieldError = ({
    field = {},
    fieldState = {},
    methods = {}
}) => {

    if (!field.name || !internals.hasFieldError({
        field,
        fieldState,
        methods
    })) {
        return;
    }

    methods.clearErrors?.(field.name);
    internals.getRevalidateOnBlurFields(methods).add(field.name);
};

internals.buildFieldInteractionProps = ({
    field = {},
    fieldProps = {},
    fieldState = {},
    methods = {}
}) => {

    if (!field.name) {
        return fieldProps;
    }

    const revalidateOnBlurFields = internals.getRevalidateOnBlurFields(methods);

    return {
        ...fieldProps,
        onBlur: (event) => {

            fieldProps.onBlur?.(event);

            if (typeof field.onBlur === 'function') {
                field.onBlur(event);
            }

            if (!revalidateOnBlurFields.has(field.name)) {
                return;
            }

            revalidateOnBlurFields.delete(field.name);
            methods.trigger?.(field.name);
        },
        onChange: (...args) => {

            internals.clearFieldError({
                field,
                fieldState,
                methods
            });

            fieldProps.onChange?.(...args);
        },
        onFocus: (event) => {

            if (!InvalidSubmitFocusUtils.shouldPreserveErrorOnFocus(event)) {
                internals.clearFieldError({
                    field,
                    fieldState,
                    methods
                });
            }

            fieldProps.onFocus?.(event);

            if (typeof field.onFocus === 'function') {
                field.onFocus(event);
            }
        }
    };
};

exports.renderControlledField = ({
    control,
    field,
    key,
    methods,
    render
}) => {

    return <Controller
        key={key}
        name={field.name}
        control={control}
        rules={exports.buildFieldRules(field)}
        render={({ field: fieldProps, fieldState }) => render({
            fieldProps: internals.buildFieldInteractionProps({
                field,
                fieldProps,
                fieldState,
                methods
            }),
            fieldState
        })}
    />;
};

exports.renderCustomComponentField = ({
    control,
    field,
    key,
    methods
}) => {

    const Custom = field.component;
    const {
        onBlur: componentOnBlur,
        onFocus: componentOnFocus,
        InputLabelProps: componentInputLabelProps,
        inputProps: componentInputProps,
        ...componentProps
    } = field.componentProps || {};
    const accessibilityProps = exports.buildFieldAccessibilityProps({
        field,
        textFieldProps: {
            InputLabelProps: componentInputLabelProps,
            inputProps: componentInputProps
        }
    });

    return exports.renderControlledField({
        control,
        field,
        key,
        methods,
        render: ({ fieldProps, fieldState }) => (
            <Custom
                {...fieldProps}
                {...accessibilityProps}
                variant={DEFAULT_FIELD_VARIANT}
                {...componentProps}
                error={fieldState.error}
                helperText={fieldState.error ? fieldState.error.message : null}
                onBlur={(event) => {
                    fieldProps.onBlur?.(event);
                    componentOnBlur?.(event);
                }}
                onFocus={(event) => {
                    fieldProps.onFocus?.(event);
                    componentOnFocus?.(event);
                }}
            />
        )
    });
};

internals.staticRenderers = {
    custom: ({ field, key, methods }) => {

        if (typeof field.render !== 'function') {
            return null;
        }

        return <React.Fragment key={key}>{field.render({ methods })}</React.Fragment>;
    },
    divider: ({ field, key }) => (
        <Divider style={{ margin: '14px 0' }} key={key} />
    ),
    explainer: ({ field, key }) => (
        <Typography
            key={key}
            variant='body1' sx={{
                color: 'text.primary',
                mb: 0,
                fontSize: 16
            }}
        >
            {field.text}
        </Typography>
    ),
    group: ({ control, field, key, methods }) => {

        const items = FormFieldUtils.normalizeFieldItems(field);

        if (!items.length) {
            return null;
        }

        return <div key={key} style={{ display: 'flex', flexFlow: 'row nowrap', gap: 8 }}>
            {items.map((child, index) => exports.renderField({
                control,
                field: child,
                key: FormFieldUtils.getFieldKey({
                    field: child,
                    fallback: `${key}-${index}`
                }),
                methods
            }))}
        </div>;
    },
    groupTitle: ({ field, key }) => (
        <Typography
            component='h3'
            key={key}
            sx={{
                alignSelf: 'center',
                bgcolor: 'background.paper',
                borderRadius: 999,
                color: 'text.primary',
                fontSize: 18,
                fontWeight: 700,
                lineHeight: '24px',
                mb: 0,
                mt: 1,
                px: 1.5,
                py: .25
            }}
        >
            {field.text}
        </Typography>
    ),
    spacer: ({ field, key }) => (
        <div key={key} style={{ height: field.height || 16 }} />
    ),
    title: ({ field, key }) => (
        <h3 key={key} style={{ marginBottom: 0 }}>{field.text}</h3>
    )
};

internals.staticRenderers.instructions = internals.staticRenderers.explainer;
internals.staticRenderers.group_title = internals.staticRenderers.groupTitle;

internals.controlledRenderers = {
    AgeField: ({ field, fieldProps, fieldState }) => (
        <AgeField
            field={field}
            value={fieldProps.value}
            setValue={fieldProps.onChange}
            onBlur={fieldProps.onBlur}
            onChange={field.onChange}
            onFocus={fieldProps.onFocus}
            textFieldProps={exports.buildTextFieldProps({
                field,
                fieldState,
                extraProps: field.textFieldProps
            })}
        />
    ),
    AutocompleteField: ({ field, fieldProps, fieldState, methods }) => (
        <AutocompleteField
            field={field}
            value={fieldProps.value}
            onBlur={fieldProps.onBlur}
            onFocus={fieldProps.onFocus}
            methods={methods}
            textFieldProps={exports.buildTextFieldProps({
                field,
                fieldState,
                extraProps: field.textFieldProps
            })}
        />
    ),
    CheckboxField: ({ field, fieldProps }) => (
        <CheckboxField
            field={field}
            value={fieldProps.value}
            setValue={fieldProps.onChange}
            onBlur={fieldProps.onBlur}
            onFocus={fieldProps.onFocus}
            onChange={field.onChange}
            checkboxProps={{
                ...field.checkboxProps,
                inputProps: exports.buildFieldInputProps({
                    field,
                    inputProps: field.checkboxProps?.inputProps
                })
            }}
        />
    ),
    DateField: ({ field, fieldProps, fieldState }) => (
        <DateField
            field={field}
            value={fieldProps.value}
            setValue={fieldProps.onChange}
            onBlur={fieldProps.onBlur}
            onChange={field.onChange}
            onFocus={fieldProps.onFocus}
            textFieldProps={exports.buildTextFieldProps({
                field,
                fieldState
            })}
        />
    ),
    DateTimeField: ({ field, fieldProps, fieldState }) => (
        <DateTimeField
            field={field}
            value={fieldProps.value}
            setValue={fieldProps.onChange}
            onBlur={fieldProps.onBlur}
            onChange={field.onChange}
            onFocus={fieldProps.onFocus}
            textFieldProps={exports.buildTextFieldProps({
                field,
                fieldState,
                extraProps: field.textFieldProps
            })}
        />
    ),
    EmojiField: ({ field, fieldProps }) => (
        <EmojiField
            field={field}
            inputProps={exports.buildFieldInputProps({
                field,
                inputProps: field.inputProps
            })}
            label={field.label}
            value={fieldProps.value}
            setValue={fieldProps.onChange}
            onBlur={fieldProps.onBlur}
            onChange={field.onChange}
            onFocus={fieldProps.onFocus}
        />
    ),
    FileField: ({ field, fieldProps, fieldState }) => (
        <FileField
            field={{
                ...field,
                inputProps: exports.buildFieldInputProps({
                    field,
                    inputProps: field.inputProps
                })
            }}
            value={fieldProps.value}
            setValue={fieldProps.onChange}
            onBlur={fieldProps.onBlur}
            onChange={field.onChange}
            onFocus={fieldProps.onFocus}
            helperText={fieldState.error ? fieldState.error.message : null}
        />
    ),
    InterestsInputField: ({ field, fieldProps, fieldState }) => (
        <ConnectedInterestsInputField
            values={fieldProps.value || []}
            isSearch={typeof field.isSearch === 'undefined' ? true : field.isSearch}
            showError={!!fieldState.error}
            errorText={fieldState.error ? fieldState.error.message : null}
            inputProps={exports.buildFieldInputProps({
                field,
                inputProps: field.inputProps
            })}
            onBlur={fieldProps.onBlur}
            onFocus={fieldProps.onFocus}
            onChange={(event, value, reason, details) => {

                fieldProps.onChange(value || []);

                if (typeof field.onChange === 'function') {
                    field.onChange(value || [], event, reason, details);
                }
            }}
            {...field.interestsInputProps}
        />
    ),
    MultiSelectField: ({ field, fieldProps, fieldState }) => {

        const labelId = internals.getFieldLabelId(field);
        const selectProps = field.selectProps || {};

        return <MultiSelectField
            field={{
                ...field,
                error: fieldState.error
            }}
            value={fieldProps.value}
            setValue={fieldProps.onChange}
            onBlur={fieldProps.onBlur}
            onChange={field.onChange}
            onFocus={fieldProps.onFocus}
            inputLabelProps={field.inputLabelProps}
            formControlProps={field.formControlProps || {
                sx: {
                    width: {
                        xs: '100%',
                        sm: '50%'
                    }
                }
            }}
            selectProps={{
                error: !!fieldState.error,
                ...selectProps,
                inputProps: exports.buildFieldInputProps({
                    field,
                    inputProps: selectProps.inputProps,
                    labelId
                })
            }}
        />;
    },
    PlaceField: ({ field, fieldProps, fieldState, methods }) => (
        <PlaceField
            field={field}
            value={fieldProps.value}
            onBlur={fieldProps.onBlur}
            onFocus={fieldProps.onFocus}
            methods={methods}
            textFieldProps={exports.buildTextFieldProps({
                field,
                fieldState,
                extraProps: field.textFieldProps
            })}
        />
    ),
    SelectField: ({ field, fieldProps, fieldState }) => {

        return <SelectField
            field={field}
            value={fieldProps.value}
            setValue={fieldProps.onChange}
            onBlur={fieldProps.onBlur}
            onChange={field.onChange}
            onFocus={fieldProps.onFocus}
            textFieldProps={exports.buildTextFieldProps({
                field,
                fieldState,
                extraProps: field.textFieldProps
            })}
        />;
    },
    SwitchField: ({ field, fieldProps }) => (
        <SwitchField
            field={field}
            value={fieldProps.value}
            setValue={fieldProps.onChange}
            onBlur={fieldProps.onBlur}
            onChange={field.onChange}
            onFocus={fieldProps.onFocus}
            switchProps={{
                ...field.switchProps,
                inputProps: exports.buildFieldInputProps({
                    field,
                    inputProps: field.switchProps?.inputProps
                })
            }}
        />
    ),
    TextField: ({ field, fieldProps, fieldState }) => (
        <TextField
            field={field}
            value={fieldProps.value}
            setValue={fieldProps.onChange}
            onBlur={fieldProps.onBlur}
            onChange={field.onChange}
            onFocus={fieldProps.onFocus}
            textFieldProps={exports.buildTextFieldProps({
                field,
                fieldState,
                extraProps: field.textFieldProps
            })}
        />
    ),
    TextareaField: ({ field, fieldProps, fieldState }) => (
        <TextareaField
            field={field}
            value={fieldProps.value}
            setValue={fieldProps.onChange}
            onBlur={fieldProps.onBlur}
            onChange={field.onChange}
            onFocus={fieldProps.onFocus}
            textFieldProps={exports.buildTextFieldProps({
                field,
                fieldState,
                extraProps: field.textFieldProps
            })}
        />
    )
};

internals.controlledRenderers.emoji = internals.controlledRenderers.EmojiField;

exports.renderRegisteredControlledField = ({
    control,
    field,
    key,
    methods,
    renderer
}) => {

    return exports.renderControlledField({
        control,
        field,
        key,
        methods,
        render: ({ fieldProps, fieldState }) => renderer({
            field,
            fieldProps,
            fieldState,
            methods
        })
    });
};

exports.getControlledRenderer = (field = {}) => internals.controlledRenderers[field.type]
    || internals.controlledRenderers[FieldTypeUtils.normalizePrimitiveType(field.type)];

exports.renderField = ({
    control,
    field,
    key,
    methods
}) => {

    if (!field) {
        return null;
    }

    if (field.component) {
        return exports.renderCustomComponentField({
            control,
            field,
            key,
            methods
        });
    }

    const staticRenderer = internals.staticRenderers[field.type];

    if (staticRenderer) {
        return staticRenderer({
            control,
            field,
            key,
            methods
        });
    }

    const controlledRenderer = exports.getControlledRenderer(field);

    if (!controlledRenderer) {
        return null;
    }

    return exports.renderRegisteredControlledField({
        control,
        field,
        key,
        methods,
        renderer: controlledRenderer
    });
};

exports.internals = internals;
exports.DEFAULT_FIELD_VARIANT = DEFAULT_FIELD_VARIANT;
